import React, { useEffect, useRef } from "react";
import { User, Wrench, Users, AtSign, Sparkles } from "lucide-react";

export default function MentionAutocomplete({
  searchQuery,
  usersList = [],
  operatorsList = [],
  teamsList = [],
  onSelect,
  onClose,
  selectedIndex = 0,
}) {
  const containerRef = useRef(null);

  // Filtra lista combinada
  const query = (searchQuery || "").toLowerCase();

  const normalizeTag = (str) =>
    (str || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  // 1. Sugestão da IA Companion
  const matchesAi =
    !query ||
    "ia".includes(query) ||
    "assistente".includes(query) ||
    "bot".includes(query) ||
    "inteligencia".includes(query);

  const aiItems = matchesAi
    ? [
        {
          id: "ai_companion",
          name: "Assistente IA Sempre Joias ✨",
          subtitle: "Inteligência Artificial • Respostas e consultas em tempo real",
          tag: "ia",
          type: "ai",
          color: "#6366f1",
        },
      ]
    : [];

  // 2. Sugestões de Equipes / Times
  const filteredTeams = teamsList.filter((team) => {
    const norm = normalizeTag(team.nome);
    return (
      !query ||
      norm.includes(query) ||
      team.nome?.toLowerCase().includes(query) ||
      team.descricao?.toLowerCase().includes(query) ||
      "equipe".includes(query) ||
      "time".includes(query)
    );
  });

  const teamItems = filteredTeams.map((team) => ({
    id: team.id,
    name: `Equipe ${team.nome}`,
    subtitle: team.descricao || "Equipe operacional • Notifica todos os membros",
    tag: `equipe_${normalizeTag(team.nome)}`,
    type: "team",
    color: "#8b5cf6",
    raw: team,
  }));

  // 3. Sugestões de Usuários
  const filteredUsers = usersList.filter(
    (u) =>
      u.name?.toLowerCase().includes(query) ||
      u.username?.toLowerCase().includes(query)
  );

  // 4. Sugestões de Operadores
  const filteredOperators = operatorsList.filter(
    (op) =>
      op.nome?.toLowerCase().includes(query) ||
      op.tipo?.toLowerCase().includes(query)
  );

  const items = [
    ...aiItems,
    ...teamItems,
    ...filteredUsers.map((u) => ({
      id: u.id,
      name: u.name || u.username,
      subtitle: u.role ? `Usuário (${u.role.toUpperCase()})` : "Usuário",
      tag: u.username || u.name?.replace(/\s+/g, "_").toLowerCase(),
      type: "user",
      color: "#3b82f6",
      raw: u,
    })),
    ...filteredOperators.map((op) => ({
      id: op.id,
      name: op.nome,
      subtitle: `Operador • ${op.tipo || "Oficina"}`,
      tag: op.nome?.replace(/\s+/g, "_").toLowerCase(),
      type: "operator",
      color: op.cor || "#10b981",
      raw: op,
    })),
  ].slice(0, 10); // Até 10 sugestões

  if (items.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 mb-2 w-76 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-50 animate-fadeIn"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider">
        <div className="flex items-center gap-1.5">
          <AtSign size={12} className="text-indigo-600" />
          <span>Mencionar no Chat</span>
        </div>
        <span className="text-[10px] text-slate-400 font-normal">Pressione Enter</span>
      </div>

      <div className="max-h-56 overflow-y-auto py-1">
        {items.map((item, idx) => {
          const isSelected = idx === selectedIndex;
          const isAI = item.type === "ai";
          const isTeam = item.type === "team";

          return (
            <button
              key={`${item.type}_${item.id}`}
              type="button"
              onClick={() => onSelect(item)}
              className={`w-full px-3 py-2 text-left flex items-center gap-2.5 transition ${
                isSelected
                  ? isAI
                    ? "bg-indigo-50/90 text-indigo-950 font-bold"
                    : isTeam
                    ? "bg-purple-50 text-purple-950 font-bold"
                    : "bg-slate-100 text-slate-900 font-bold"
                  : isAI
                  ? "hover:bg-indigo-50/50 text-indigo-900"
                  : isTeam
                  ? "hover:bg-purple-50/50 text-purple-900"
                  : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-xs ${
                  isAI
                    ? "bg-gradient-to-br from-indigo-500 to-purple-600"
                    : isTeam
                    ? "bg-gradient-to-br from-purple-600 to-violet-700"
                    : ""
                }`}
                style={{ backgroundColor: isAI || isTeam ? undefined : item.color }}
              >
                {isAI ? (
                  <Sparkles size={13} />
                ) : isTeam ? (
                  <Users size={13} />
                ) : item.type === "operator" ? (
                  <Wrench size={13} />
                ) : (
                  <User size={13} />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate leading-tight flex items-center gap-1">
                  {item.name}
                  {isAI && (
                    <span className="bg-indigo-100 text-indigo-700 text-[9px] font-black px-1.5 py-0.2 rounded border border-indigo-200">
                      IA
                    </span>
                  )}
                  {isTeam && (
                    <span className="bg-purple-100 text-purple-700 text-[9px] font-black px-1.5 py-0.2 rounded border border-purple-200">
                      TIME
                    </span>
                  )}
                </p>
                <span className="text-[10px] text-slate-400 block truncate">{item.subtitle}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
